/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  FiSearch,
  FiX,
  FiExternalLink,
  FiFilter,
  FiList,
  FiGrid,
} from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { PRODUCT_LIST } from "../../constant";
import { GET } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { sanitizeImageUrl } from "../../utils/sanitizeImageUrl";
import {
  buildListingPreview,
  buildProductLocation,
} from "../../utils/productLocation";
import DetailDrawer from "../../components/DetailDrawer";
import Auctionlist from "../../components/Acution Listing/Filters";

const STATUS_TABS = [
  { key: "All Auction", label: "All", status: "Upcoming Auction,Live Auction" },
  { key: "Live Auction", label: "Live", status: "Live Auction" },
  { key: "Upcoming Auction", label: "Upcoming", status: "Upcoming Auction" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const BASE_FILTER_KEYS = new Set([
  "page",
  "page_size",
  "timezone",
  "sort_cars",
  "user_id",
  "auction_status",
]);

function formatMoney(value) {
  if (value == null || value === "" || value === "N/A") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

function countAdvancedFilters(filterRef) {
  const current = filterRef?.current || {};
  return Object.keys(current).filter((key) => {
    if (BASE_FILTER_KEYS.has(key)) return false;
    const value = current[key];
    return value != null && value !== "";
  }).length;
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.product_name,
    item?.brands,
    item?.model_name,
    item?.fuel_type,
    item?.year,
    item?.Registration,
    item?.registration,
    item?.id,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function statusChipClass(status) {
  if (status === "Live") return "dd-chip--live";
  if (status === "Upcoming") return "dd-chip--upcoming";
  return "dd-chip--auction";
}

function LotActions({ item, onView, onBid }) {
  return (
    <div className="dd-actions">
      <button
        type="button"
        className="dd-action"
        title="View details"
        onClick={() => onView(item)}
      >
        <FiExternalLink size={14} aria-hidden />
        <span>View</span>
      </button>
      {item?.status === "Live" ? (
        <button
          type="button"
          className="dd-action"
          title="Open listing"
          onClick={() => onBid(item)}
        >
          <span>Bid</span>
        </button>
      ) : null}
    </div>
  );
}

function AuctionDashboard({ product, getProductList }) {
  const history = useHistory();
  const [tabIdx, setTabIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState("1");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewMode, setViewMode] = useState("list");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterTick, setFilterTick] = useState(0);
  const [viewingItem, setViewingItem] = useState(null);

  const selectedFilter = useRef({
    page: 1,
    page_size: 10,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    sort_cars: "1",
    auction_status: STATUS_TABS[0].status,
  });
  const appliedFilterSnapshot = useRef(null);

  const tab = STATUS_TABS[tabIdx];
  const lots = Array.isArray(product?.data?.data) ? product.data.data : [];
  const loading = Boolean(product?.fetchingList);
  const total = Number(product?.data?.total_object) || 0;
  const pageCount = Math.max(1, Number(product?.data?.num_of_pages) || 1);

  const buildQuery = () =>
    Object.keys(selectedFilter.current)
      .map(
        (key) =>
          `${key}=${encodeURIComponent(selectedFilter.current[key] ?? "")}`
      )
      .join("&");

  const refreshList = () => {
    getProductList(buildQuery());
    setFilterTick((n) => n + 1);
  };

  const openFilters = () => {
    appliedFilterSnapshot.current = { ...selectedFilter.current };
    setFiltersOpen(true);
  };

  const closeFiltersWithoutApply = () => {
    if (appliedFilterSnapshot.current) {
      selectedFilter.current = { ...appliedFilterSnapshot.current };
      appliedFilterSnapshot.current = null;
    }
    setFiltersOpen(false);
    setFilterTick((n) => n + 1);
  };

  const applyFilters = () => {
    selectedFilter.current.page = 1;
    setPage(1);
    appliedFilterSnapshot.current = null;
    setFiltersOpen(false);
    refreshList();
  };

  useEffect(() => {
    selectedFilter.current.auction_status = tab.status;
    selectedFilter.current.sort_cars = sortValue;
    selectedFilter.current.page_size = pageSize;
    selectedFilter.current.page = 1;
    setPage(1);
    setSearch("");
    setViewingItem(null);
    refreshList();
  }, [tabIdx, sortValue, pageSize]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeFiltersWithoutApply();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtersOpen]);

  const filteredLots = useMemo(
    () => lots.filter((item) => matchesSearch(item, search)),
    [lots, search]
  );

  void filterTick;
  const advancedCount = countAdvancedFilters(selectedFilter);
  const showingFrom = total ? (page - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(page * pageSize, total);

  const openListing = (item) => {
    if (!item?.id || item?.status !== "Live") return;
    history.push(
      buildProductLocation("/dealers/dashbord/product", item.id, {
        listingPreview: buildListingPreview(item),
      })
    );
  };

  const lotMeta = (item) => {
    const title = item?.product_name || "Untitled lot";
    const image = sanitizeImageUrl(item?.main_image);
    const specs = [
      item?.brands,
      item?.model_name,
      item?.fuel_type,
      item?.mileage ? `${item.mileage}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return { title, image, specs };
  };

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Auction status">
        {STATUS_TABS.map((item, index) => (
          <button
            key={item.key}
            type="button"
            className={`dd-tab${index === tabIdx ? " is-active" : ""}`}
            onClick={() => setTabIdx(index)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="dd-toolbar dd-toolbar--floor">
        <label className="dd-toolbar__search">
          <FiSearch size={16} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, make, model, reg…"
            aria-label="Search auctions"
          />
          {search ? (
            <button
              type="button"
              className="dd-toolbar__clear-input"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          ) : null}
        </label>

        <label className="dd-toolbar__field">
          <span>Sort</span>
          <select
            value={sortValue}
            onChange={(e) => setSortValue(e.target.value)}
          >
            <option value="1">Price · low to high</option>
            <option value="2">Price · high to low</option>
            <option value="3">Mileage · low to high</option>
            <option value="4">Mileage · high to low</option>
            <option value="5">Make · A–Z</option>
            <option value="6">Make · Z–A</option>
            <option value="7">Ending next</option>
            <option value="8">Ending last</option>
          </select>
        </label>

        <label className="dd-toolbar__field">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <label className="dd-toolbar__field">
          <span>View</span>
          <div className="dd-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`dd-view-toggle__btn${
                viewMode === "list" ? " is-active" : ""
              }`}
              onClick={() => setViewMode("list")}
              aria-pressed={viewMode === "list"}
            >
              <FiList size={15} aria-hidden />
              List
            </button>
            <button
              type="button"
              className={`dd-view-toggle__btn${
                viewMode === "grid" ? " is-active" : ""
              }`}
              onClick={() => setViewMode("grid")}
              aria-pressed={viewMode === "grid"}
            >
              <FiGrid size={15} aria-hidden />
              Grid
            </button>
          </div>
        </label>

        <label className="dd-toolbar__field">
          <span>More</span>
          <button
            type="button"
            className="dd-toolbar__action-btn"
            onClick={openFilters}
          >
            <FiFilter size={15} aria-hidden />
            Filters
            {advancedCount > 0 ? (
              <span className="dd-toolbar__action-count">{advancedCount}</span>
            ) : null}
          </button>
        </label>
      </div>

      <div className="dd-toolbar__meta">
        {loading && !lots.length ? (
          <span>Loading auctions…</span>
        ) : filteredLots.length ? (
          <span>
            Showing {showingFrom}–{showingTo} of {total}
            {search ? ` · ${filteredLots.length} match on this page` : ""}
          </span>
        ) : (
          <span>No matching lots</span>
        )}
      </div>

      {loading && !lots.length ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading auctions…</strong>
          <p>Fetching lots for the {tab.label.toLowerCase()} floor.</p>
        </div>
      ) : !lots.length ? (
        <div className="dd-empty">
          <strong>No {tab.label.toLowerCase()} lots</strong>
          <p>
            Try another status tab or open Filters to widen make, model, and
            mileage.
          </p>
        </div>
      ) : !filteredLots.length ? (
        <div className="dd-empty">
          <strong>No matches</strong>
          <p>Try a different search on this page.</p>
        </div>
      ) : viewMode === "list" ? (
        <>
          <div className="dd-table-wrap dd-fav-table">
            <table className="dd-table">
              <thead>
                <tr>
                  <th scope="col">Vehicle</th>
                  <th scope="col">Status</th>
                  <th scope="col">Current bid</th>
                  <th scope="col">Year</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLots.map((item, index) => {
                  const { title, image, specs } = lotMeta(item);
                  return (
                    <tr key={item?.id || index}>
                      <td>
                        <div className="dd-fav-vehicle">
                          <span className="dd-fav-vehicle__thumb" aria-hidden>
                            {image ? (
                              <img src={image} alt="" />
                            ) : (
                              <span className="dd-fav-vehicle__placeholder">
                                —
                              </span>
                            )}
                          </span>
                          <div className="dd-fav-vehicle__copy">
                            <strong>{title}</strong>
                            {specs ? <span>{specs}</span> : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`dd-chip ${statusChipClass(item?.status)}`}
                        >
                          {item?.status || "—"}
                        </span>
                      </td>
                      <td>{formatMoney(item?.current_bid)}</td>
                      <td>{item?.year || "—"}</td>
                      <td>
                        <LotActions
                          item={item}
                          onView={setViewingItem}
                          onBid={openListing}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="dd-floor-grid">
          {filteredLots.map((item, index) => {
            const { title, image, specs } = lotMeta(item);
            return (
              <article key={item?.id || index} className="dd-floor-tile">
                <div className="dd-floor-tile__media" aria-hidden>
                  {image ? (
                    <img src={image} alt="" />
                  ) : (
                    <span className="dd-fav-vehicle__placeholder">—</span>
                  )}
                </div>
                <div className="dd-floor-tile__body">
                  <span
                    className={`dd-chip ${statusChipClass(item?.status)}`}
                  >
                    {item?.status || "—"}
                  </span>
                  <strong className="dd-floor-tile__title">{title}</strong>
                  {specs ? (
                    <span className="dd-floor-tile__specs">{specs}</span>
                  ) : null}
                  <div className="dd-floor-tile__bid">
                    <span>Current bid</span>
                    <strong>{formatMoney(item?.current_bid)}</strong>
                  </div>
                  <LotActions
                    item={item}
                    onView={setViewingItem}
                    onBid={openListing}
                  />
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!loading && filteredLots.length && pageCount > 1 ? (
        <div className="dd-pagination">
          <PaginationControl
            page={page}
            between={3}
            total={total}
            limit={pageSize}
            changePage={(next) => {
              setPage(next);
              selectedFilter.current.page = next;
              refreshList();
            }}
            ellipsis={1}
          />
        </div>
      ) : null}

      <DetailDrawer
        item={viewingItem}
        eyebrow={`Auction · ${viewingItem?.status || tab.label}`}
        onClose={() => setViewingItem(null)}
        footer={
          viewingItem?.status === "Live" && viewingItem?.id ? (
            <button
              type="button"
              className="dd-btn dd-btn--primary"
              onClick={() => {
                openListing(viewingItem);
                setViewingItem(null);
              }}
            >
              Open listing & bid
            </button>
          ) : null
        }
      />

      {filtersOpen ? (
        <div
          className="dd-drawer dd-filters-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Auction filters"
        >
          <button
            type="button"
            className="dd-drawer__backdrop"
            aria-label="Close filters"
            onClick={closeFiltersWithoutApply}
          />
          <aside className="dd-drawer__panel">
            <header className="dd-drawer__head">
              <div>
                <p className="dd-drawer__eyebrow">Auctions</p>
                <h2>Filters</h2>
                <p className="dd-drawer__reg">
                  Make, model, fuel, mileage, and more
                </p>
              </div>
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={closeFiltersWithoutApply}
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </header>
            <div className="dd-drawer__body dd-filters-drawer__body">
              <Auctionlist
                isBuyNow={false}
                hideHeader
                handleDrawer={closeFiltersWithoutApply}
                setPage={setPage}
                selectedFilter={selectedFilter}
              />
            </div>
            <div className="dd-drawer__foot">
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={closeFiltersWithoutApply}
              >
                Close
              </button>
              <button
                type="button"
                className="dd-btn dd-btn--primary"
                onClick={applyFilters}
              >
                Apply filters
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  product: state.product,
});

const mapDispatchToProps = (dispatch) => ({
  getProductList: (params) =>
    dispatch({
      type: PRODUCT_LIST,
      payload: {
        type: PRODUCT_LIST,
        method: GET,
        label: `${Labels.product_list}?${params}`,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuctionDashboard);
